// ============================================
// API Configuration
// ============================================

export const SUPABASE_PLACEHOLDER_URL = 'https://placeholder.supabase.co';
export const SUPABASE_PLACEHOLDER_KEY = 'placeholder';

// ============================================
// Storage Keys
// ============================================

export const STORAGE_KEYS = {
  USERS: 'nexus_users',
  PROJECTS: 'nexus_projects',
  TASKS: 'nexus_tasks',
  MESSAGES: 'nexus_messages',
  NOTIFICATIONS: 'nexus_notifications',
  CURRENT_USER: 'nexus_auth_token',
  TIMELINE: 'nexus_timeline',
} as const;

// ============================================
// Error Messages
// ============================================

export const ERROR_MESSAGES = {
  AUTH: {
    INVALID_CREDENTIALS: 'Authentication failed. Please check your credentials.',
    SESSION_EXPIRED: 'Your session has expired. Please log in again.',
    UNAUTHORIZED: 'You do not have permission to perform this action.',
    USER_EXISTS: 'This user already exists or signups are disabled.',
    SIGNUP_DISABLED: 'Invitee already exists or signups are disabled in Supabase.',
  },
  NETWORK: {
    CONNECTION_FAILED: 'Unable to connect to the server. Please check your internet connection.',
    TIMEOUT: 'Request timed out. Please try again.',
    SERVER_ERROR: 'An unexpected server error occurred.',
  },
  VALIDATION: {
    REQUIRED_FIELD: 'This field is required.',
    INVALID_EMAIL: 'Please enter a valid email address.',
    PASSWORD_TOO_SHORT: 'Password must be at least 8 characters.',
  },
  PROJECT: {
    NOT_FOUND: 'Project not found.',
    DELETE_FAILED: 'Failed to delete project.',
    UPDATE_FAILED: 'Failed to update project.',
  },
  TASK: {
    NOT_FOUND: 'Task not found.',
    DELETE_FAILED: 'Failed to delete task.',
    UPDATE_FAILED: 'Failed to update task.',
    CREATE_FAILED: 'Failed to create task.',
  },
} as const;

// ============================================
// Success Messages
// ============================================

export const SUCCESS_MESSAGES = {
  TASK: {
    CREATED: '🚀 Task live on Cloud!',
    UPDATED: 'Task updated successfully.',
    DELETED: 'Task deleted successfully.',
  },
  PROJECT: {
    CREATED: 'Project created successfully.',
    UPDATED: 'Settings saved to cloud.',
    ARCHIVED: 'Project archived.',
    RESTORED: 'Project restored.',
    DELETED: 'Project deleted successfully.',
  },
  USER: {
    INVITED: 'Invitation sent successfully.',
    UPDATED: 'User updated successfully.',
  },
} as const;

// ============================================
// UI Constants
// ============================================

export const PRIORITY_COLORS = {
  High: 'bg-rose-500',
  Medium: 'bg-amber-500',
  Low: 'bg-sky-500',
  default: 'bg-slate-400',
} as const;

export const STATUS_COLORS = {
  TODO: 'bg-slate-100 text-slate-600',
  IN_PROGRESS: 'bg-indigo-100 text-indigo-700',
  DONE: 'bg-emerald-100 text-emerald-700',
} as const;

// ============================================
// Pagination Defaults
// ============================================

export const PAGINATION = {
  DEFAULT_PAGE: 0,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
} as const;

// ============================================
// Date/Time Constants
// ============================================

export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  INPUT: 'yyyy-MM-dd',
  TIME: 'HH:mm',
  DATETIME: 'MMM dd, yyyy HH:mm',
} as const;

// ============================================
// Debounce/Throttle Delays (ms)
// ============================================

export const DELAYS = {
  SEARCH_DEBOUNCE: 300,
  AUTO_SAVE: 1000,
  TOAST_DURATION: 3000,
  ANIMATION_DURATION: 300,
} as const;

// ============================================
// HTTP Status Codes
// ============================================

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
} as const;
